import React from 'react';
import { useSelector } from 'react-redux';
import { useFormikContext } from 'formik';

import { StoreShape } from 'hellospa/redux/types';
import * as types from 'hellospa/page/prep-and-send/data/types';

type OnComplete = (values?: any) => void;

type Stage = {
  name: types.TriggerStage,
  components: types.TriggerComponent[],
  onComplete: OnComplete,
  onCancel: () => void
}

type SubmitFunc = (...args: any[]) => Promise<unknown> | void;

/**
 * This tool takes a list of components and callbacks to automate
 * rendering them at specified stages. "Triggers". Components require
 * Formik context so you must render the result inside a <Formik> component
 */
export default function useTriggers<TSubmit extends SubmitFunc>(
  stageComponents: types.TriggerStageComponents,

  // useTriggers is generally called like this:
  //
  // const [Triggers, setStage] = useTriggers(triggers, {
  //   load: () => {},
  //   submit: () => {},
  //   done: () => {},
  // })
  //
  // On every render, those are 3 new functions and a new `callbacks` object.
  // That's fine, but it means if we use any part of `callbacks` in a dependency
  // array for useCallback or useMemo, they get regenerated on every render. For
  // that reason, I've removed (almost) all of the useCallback and useMemo
  // caching in useTriggers.
  callbacks: types.TriggerCallback<TSubmit>,
): [React.FC, typeof callbacks] {

  const [stage, setStage] = React.useState<Stage>({
    name: 'load',
    components: stageComponents.load,
    onComplete: () => callbacks.load(),
    onCancel: () => { },
  });

  const [skipped, setSkipped] = React.useState<string[]>([]);

  /**
   * At developer-defined points in components updateStage can be called to define the
   * new stage and any parameters the onComplete callback requires
   */
  const updateStage = {
    load: () => {
      setStage((current) => {
        current.onCancel();
        return {
          name: 'load',
          components: stageComponents.load,
          onComplete: () => callbacks.load(),
          onCancel: () => { },
        };
      });
    },
    submit: (...parameters: Parameters<TSubmit>) => {
      return new Promise(((resolve) => {
        setStage((current) => {
          current.onCancel();
          return {
            name: 'submit',
            components: stageComponents.submit,
            onComplete: () => resolve(callbacks.submit(...parameters)),
            onCancel: () => resolve(),
          };
        });
      }));
    },
    done: () => {
      setStage((current) => {
        current.onCancel();
        return {
          name: 'done',
          components: stageComponents.done,
          onComplete: () => callbacks.done(),
          onCancel: () => { },
        };
      });
    },
  };

  /**
   * Some components are optional and can be skipped.
   *
   * Skipping hides the component and displays the next one in the stage list.
   * If no further components are in stage list the stage.onComplete is triggered.
   */
  const onSkip = (stage: Stage, index: number) => {
    setSkipped((prevState) => prevState.concat(`${stage.name}-${index}`));
  };

  /**
   * Some components are cancellable.
   *
   * Cancelling immediately stops looping through stage components
   * and reverts to initial load stage.
   *
   * The current stage.onComplete is NOT triggered but
   * load's stage.onComplete will be triggered.
   *
   * Components in all stages that were previously skipped will
   * remain skipped and will not be shown to the user again.
   */
  const onCancel = () => {
    updateStage.load();
  };

  const TriggerRef = React.useRef<JSX.Element>(<div />);
  /**
   * We wrap Triggers in a callback so the component is not actually rendered at this point
   * but allows the developer to render as any normal React component: <Triggers />
   * vs having to print it as a variable: {Triggers}
   *
   * On each render React will check whether `<Triggers />` refers to the same
   * function (object identity). If React encounters a new function, it has to
   * assume it's a new component and remount it. By using a useCallback with an
   * empty dependency array, this will return the same function for the
   * lifetime of useTriggers.
   */
  const Trigger = React.useCallback(() => {
    return TriggerRef.current;
  }, []);
  // Using a ref allows me to hide all these props from useCallback's
  // dependencies array. The JSX tag doesn't call the component's code, it gets
  // called later when the tag gets returned from a render function (Trigger
  // above).
  TriggerRef.current = (
    <Triggers
      key={stage.name}
      stage={stage}
      skipped={skipped}
      onCancel={onCancel}
      onSkip={onSkip} />
  );

  return [
    Trigger,
    // @ts-ignore Can't get TS to not complain about this generic, but it's working correctly
    updateStage,
  ];
}

type TriggersProps = {
  stage: Stage,
  skipped: string[],
  onSkip: (stage: Stage, index: number) => void,
  onCancel: () => void,
}

const Triggers = ({ stage, skipped, onSkip, onCancel }: TriggersProps) => {
  const state = useSelector((state: StoreShape) => state);
  const formik = useFormikContext();

  let Show: JSX.Element | null = null;
  // default to an empty array if undefined for some reason
  const components = stage.components || [];

  for (let i = 0; i < components.length; i++) {
    const Component = components[i];
    const needsTrigger = Component.needsTrigger(state, formik);
    const callSkip = () => onSkip(stage, i);

    if (skipped.includes(`${stage.name}-${i}`)) {
      // eslint-disable-next-line no-continue
      continue;
    }

    /**
     * We only want to render the first component that needsTrigger
     */
    if (needsTrigger) {
      const index = i;
      Show = <Component
        key={`trigger-${index}`}
        onCancel={onCancel}
        onSkip={callSkip}
        stage={stage.name} />;

      break;
    }
  }

  /**
   * If no components are available for rendering we can call
   * the onComplete callback
   */
  React.useEffect(() => {
    if (!Show) {
      stage.onComplete();
    }
  }, [Show, stage]);

  return Show;
};
